import { ChangeEvent } from "react";
import styled, { css } from "styled-components";
import "../../designTokens/variables.css";
import {
  primaryActive,
  primaryDisabled,
  primaryInactive,
} from "../../typography/alpha";
import { Icon } from "../Icon";
import { IconLabelToggleProps, IconLabelToggleVariant } from "./types";

const disabledStyle = css`
  ${primaryDisabled.body3}
  * {
    fill: var(--color-primary-200);
  }
`;

const inactiveStyle = css`
  ${primaryInactive.body3}
  * {
    fill: var(--color-primary-400);
  }
`;

type StyledContentsProps = {
  variant: IconLabelToggleVariant;
};

const StyledContents = styled.div<StyledContentsProps>`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;
  background: none;
  padding: 0 12px;
  align-items: center;
  display: inline-flex;
  ${(props: StyledContentsProps) =>
    props.variant === "DEFAULT"
      ? primaryActive.body3
      : props.variant === "DISABLED"
        ? disabledStyle
        : inactiveStyle}
`;

const HiddenCheckbox = styled.input`
  cursor: pointer;
  position: absolute;
  opacity: 0;
  z-index: 1;
  display: block;
  margin: 0;
  height: 100%;
  width: 100%;
  :disabled {
    cursor: not-allowed;
  }
`;

const ToggleContainer = styled.div`
  position: relative;
  display: inline-block;
  height: 40px;

  ${HiddenCheckbox}:checked + ${StyledContents} {
    color: var(--color-secondary-800);
    * {
      fill: var(--color-secondary-800);
    }
  }
`;

const TextContainer = styled.div`
  padding-left: 4px;
`;

export const IconLabelToggle = ({
  onToggle = () => {},
  label,
  iconName,
  variant = "DEFAULT",
  ...props
}: IconLabelToggleProps) => {
  const disabledOrInactive = variant === "DISABLED" || variant === "INACTIVE";

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabledOrInactive) {
      return;
    }
    onToggle({
      isOn: e.target.checked,
      event: e,
    });
  };

  return (
    <ToggleContainer>
      <HiddenCheckbox
        aria-label={label}
        {...props}
        type="checkbox"
        disabled={disabledOrInactive}
        onChange={onChange}
      />
      <StyledContents variant={variant}>
        <Icon iconName={iconName} />
        <TextContainer>{label}</TextContainer>
      </StyledContents>
    </ToggleContainer>
  );
};
